import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, CircularProgress, List, ListItem, ListItemText, ListItemSecondaryAction, Divider,  } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Build as InstallerIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const Installers = () => {
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstaller, setEditingInstaller] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', mobile: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInstallers();
  }, []);

  const fetchInstallers = async () => {
    try {
      const response = await client.get('/orders/installers/');
      setInstallers(response.data);
    } catch (error) {
      toast.error('خطا در دریافت لیست نصاب‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (installer = null) => {
    if (installer) {
      setEditingInstaller(installer);
      setFormData({ full_name: installer.full_name, mobile: installer.mobile });
    } else {
      setEditingInstaller(null);
      setFormData({ full_name: '', mobile: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInstaller(null);
    setFormData({ full_name: '', mobile: '' });
  };

  const handleSave = async () => {
    if (!formData.full_name.trim()) {
      toast.error('نام و نام خانوادگی الزامی است');
      return;
    }
    if (!formData.mobile || !/^09\d{9}$/.test(formData.mobile)) {
      toast.error('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
      return;
    }

    setSaving(true);
    try {
      if (editingInstaller) {
        await client.put(`/orders/installers/${editingInstaller.id}/`, formData);
        toast.success('نصاب ویرایش شد');
      } else {
        await client.post('/orders/installers/create/', formData);
        toast.success('نصاب اضافه شد');
      }
      handleCloseDialog();
      fetchInstallers();
    } catch (error) {
      toast.error('خطا در ذخیره اطلاعات');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (installer) => {
    if (!window.confirm(`آیا از حذف "${installer.full_name}" مطمئن هستید؟`)) return;

    try {
      await client.delete(`/orders/installers/${installer.id}/`);
      toast.success('نصاب حذف شد');
      fetchInstallers();
    } catch (error) {
      toast.error('خطا در حذف نصاب');
    }
  };

  

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: themeColors.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* هدر */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InstallerIcon sx={{ color: themeColors.gold }} />
        <Typography variant="h6" fontWeight="bold">
          نصاب‌ها
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ({toPersianDigits(installers.length.toString())})
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: themeColors.gold,
            color: themeColors.dark,
            '&:hover': { bgcolor: themeColors.goldLight },
          }}
        >
          افزودن نصاب
        </Button>
      </Box>

      {/* لیست نصاب‌ها */}
      {installers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <InstallerIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              هنوز نصابی ثبت نشده
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2, borderColor: themeColors.gold, color: themeColors.gold }}
            >
              افزودن اولین نصاب
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <List disablePadding>
            {installers.map((installer, index) => (
              <React.Fragment key={installer.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold">
                        {installer.full_name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" dir="ltr" sx={{ textAlign: 'right' }}>
                          {toPersianDigits(installer.mobile)}
                        </Typography>
                        {installer.orders_count > 0 && (
                          <Typography variant="caption" color="primary">
                            • {toPersianDigits(installer.orders_count.toString())} سفارش
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleOpenDialog(installer)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(installer)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* دیالوگ افزودن/ویرایش */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: themeColors.dark, color: themeColors.gold }}>
          {editingInstaller ? 'ویرایش نصاب' : 'افزودن نصاب'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <CustomTextField             fullWidth
            label="نام و نام خانوادگی"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
          <CustomTextField             fullWidth
            label="شماره موبایل"
            value={formData.mobile}
            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
            placeholder="09123456789"
            inputProps={{ maxLength: 11, dir: 'ltr' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: themeColors.gold,
              color: themeColors.dark,
              '&:hover': { bgcolor: themeColors.goldLight },
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Installers;